import React, {FC} from 'react';
import styled, {css} from 'styled-components';

import {fromDefinitionCondition} from '../../rules/ruleConditionHelpers';
import {DropdownGroup} from '../../rules/ruleEditor/dropdowns/multipleGroupsDropdownHelpers';
import {RuleConditionCardProps} from '../../rules/ruleEditor/ruleConditionCard';
import {RuleConditionDefinition} from '../../rules/ruleParameterModel';
import {useBaseEditorContext} from '../baseEditorContext';
import {ConditionAddBlock, ConditionsAddBlockTypesEnum} from './conditionAddBlock';
import {ConditionsAndBlock} from './conditionsAndBlock';
import {ConditionsStateActions} from './conditionsState';

/*
 * Props.
 */

interface ConditionsListProps {
  isAdvancedEditor: boolean;
  shouldHideAdvancedAddBlock?: boolean;

  conditions: ReadonlyArray<ReadonlyArray<RuleConditionCardProps>>;
  conditionGroups: ReadonlyArray<DropdownGroup<RuleConditionDefinition>>;

  onAddCondition: ConditionsStateActions['onAddCondition'];
}

/*
 * Style.
 */

interface ConditionsListWrapperStyleProps {
  $isAdvanced: boolean;
}

const ConditionsListWrapperDiv = styled.div<ConditionsListWrapperStyleProps>`
  display: grid;
  grid-auto-rows: min-content;
  grid-row-gap: 5px;
  ${({$isAdvanced}) =>
    $isAdvanced &&
    css`
      grid-row-gap: 0;
    `}
`;

/*
 * Component.
 */

export const ConditionsList: FC<ConditionsListProps> = (props) => {
  const {isAdvancedEditor, shouldHideAdvancedAddBlock, conditions, conditionGroups, onAddCondition} = props;

  const {isReadOnly} = useBaseEditorContext();

  const canUpdateConditions = isAdvancedEditor && !isReadOnly;

  return (
    <ConditionsListWrapperDiv $isAdvanced={isAdvancedEditor}>
      {conditions.map((nestedConditions, i) => (
        <ConditionsAndBlock
          key={getConditionBlockKey(nestedConditions)}
          isAdvancedEditor={isAdvancedEditor}
          shouldDisplayAdvancedAndBlock={
            !shouldHideAdvancedAddBlock && !(!isAdvancedEditor && i >= conditions.length - 1)
          }
          canUpdateConditions={canUpdateConditions}
          conditions={nestedConditions}
          conditionGroups={conditionGroups}
          onAddCondition={(newCondition) => onAddCondition(i, fromDefinitionCondition(newCondition))}
        />
      ))}
      {canUpdateConditions && (
        <ConditionAddBlock
          type={ConditionsAddBlockTypesEnum.AND}
          shouldShowAdvanced={isAdvancedEditor}
          conditionGroups={conditionGroups}
          onAddCondition={(newCondition) =>
            onAddCondition(conditions.length, fromDefinitionCondition(newCondition))
          }
        />
      )}
    </ConditionsListWrapperDiv>
  );
};

/*
 * Helpers.
 */

function getConditionBlockKey(conditions: ReadonlyArray<RuleConditionCardProps>) {
  return `list-conditions-${conditions.map((c) => c.condition.clientId).join('-')}`;
}
